// Mes réservations d'équipements (portail, M19) : à venir puis passées, état, montant ; une réservation
// acceptée à payer renvoie vers sa créance (paiement par la rubrique Paiements et le simulateur hors
// production) ; refus avec son motif ; annulation par l'usager tant que la réservation n'est pas passée.
import { formaterFrancs } from '@cc/contrats';
import { Bandeau, ConfirmationIntegree, EtatVide, LienBouton } from '@cc/ui';
import { CalendarRange } from 'lucide-react';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { EnTeteCitoyen, ExigeConnexion, Section, TexteStatut } from '../commun';
import { CLES_PAIEMENTS } from '../paiements/api';
import { annulerMaReservation, CLES_RESERVATIONS, STATUT_RESERVATION, useMesReservations, type ReservationUsager } from './api';
import styles from '../citoyen.module.css';

const aujourdhui = () => new Date(Date.now() + 3_600_000).toISOString().slice(0, 10);

function CarteReservation({ r }: { r: ReservationUsager }) {
  const annulation = useAction({ executer: () => annulerMaReservation(r.id, 'Annulée par l\'usager depuis le portail'), succes: 'Réservation annulée.', invalider: [[...CLES_RESERVATIONS.mes], [...CLES_PAIEMENTS.creances]] });
  const statut = STATUT_RESERVATION[r.etat];
  const annulable = ['demandee', 'a_payer', 'confirmee'].includes(r.etat) && r.date >= aujourdhui();
  return (
    <li className={styles.carte} aria-label={`Réservation ${r.numero}`}>
      <div className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>{r.equipement.nom} · {date(r.date)} de {r.heure_debut} à {r.heure_fin}</span>
        <span className={styles.ligneSous}>{r.numero} · {r.collectivite.nom} · {r.objet}</span>
        {r.creance ? <span className={styles.ligneSous}>Montant : {formaterFrancs(r.creance.montant_du)}{r.fictif ? ' (fictif)' : ''}{r.creance.reste > 0 ? `, reste ${formaterFrancs(r.creance.reste)}` : ', payé'}</span> : r.montant_estime !== null ? <span className={styles.ligneSous}>Montant estimé : {formaterFrancs(r.montant_estime)}{r.fictif ? ' (fictif)' : ''}, dû après acceptation</span> : null}
        {r.motif && (r.etat === 'refusee' || r.etat === 'annulee') ? <span className={styles.ligneSous}>Motif : {r.motif}</span> : null}
        <TexteStatut libelle={statut.libelle} ton={statut.ton} />
      </div>
      {r.etat === 'a_payer' && r.creance ? (
        <div className={styles.ligneBoutons}>
          <LienBouton href={`/citoyen/paiements/creances/${r.creance.id}`} variante="principal">Payer la réservation</LienBouton>
        </div>
      ) : null}
      {annulation.messageErreur ? <Bandeau type="erreur">{annulation.messageErreur}</Bandeau> : null}
      {annulable ? <ConfirmationIntegree libelle="Annuler cette réservation" petit question="Annuler cette réservation ? Le créneau sera libéré." libelleConfirmer="Confirmer l'annulation" enCours={annulation.enCours} onConfirmer={() => annulation.lancer(undefined)} /> : null}
    </li>
  );
}

export function PageMesReservations() {
  const liste = useMesReservations();
  const j = aujourdhui();
  const aVenir = (liste.data ?? []).filter((r) => r.date >= j && !['refusee', 'annulee', 'terminee'].includes(r.etat)).sort((a, b) => `${a.date}${a.heure_debut}`.localeCompare(`${b.date}${b.heure_debut}`));
  const autres = (liste.data ?? []).filter((r) => !aVenir.includes(r));
  return (
    <>
      <EnTeteCitoyen titre="Réservations" sousTitre="Salles et terrains de votre collectivité : demande, paiement, suivi." actions={<LienBouton href="/citoyen/reservations/nouvelle" variante="principal">Réserver un équipement</LienBouton>} />
      <ExigeConnexion titre="Connectez-vous pour voir vos réservations">
        <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="Vos réservations" lignes={4}>
          <Section titre="À venir">
            {aVenir.length === 0 ? (
              <EtatVide titre="Aucune réservation à venir" icone={CalendarRange}><p>Choisissez un équipement et un créneau libre. Un rappel vous est envoyé la veille.</p></EtatVide>
            ) : (
              <ul className={styles.pile} aria-label="Réservations à venir">{aVenir.map((r) => <CarteReservation key={r.id} r={r} />)}</ul>
            )}
          </Section>
          {autres.length > 0 ? (
            <Section titre="Passées, refusées ou annulées">
              <ul className={styles.pile} aria-label="Autres réservations">{autres.map((r) => <CarteReservation key={r.id} r={r} />)}</ul>
            </Section>
          ) : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
